import React, { useEffect, useState } from 'react';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { REVIEW_COLORS, TAG_EMOJIS } from '../constants/reviewConstants';

interface QuickRatePlayer {
  id: string;
  name: string;
  avatar?: string;
}

interface ExistingReview {
  positiveTags: string[];
  negativeTags: string[];
}

interface QuickRateSheetProps {
  isOpen: boolean;
  player: QuickRatePlayer | null;
  positiveTags: string[];
  negativeTags: string[];
  existingReview: ExistingReview | null;
  onClose: () => void;
  onSave: (selected: { positive: string[]; negative: string[] }) => Promise<void> | void;
}

const MAX_TAGS_TOTAL = 3;

export const QuickRateSheet: React.FC<QuickRateSheetProps> = ({
  isOpen,
  player,
  positiveTags,
  negativeTags,
  existingReview,
  onClose,
  onSave,
}) => {
  const { isMobile } = useResponsive();
  const [selectedPositive, setSelectedPositive] = useState<string[]>([]);
  const [selectedNegative, setSelectedNegative] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedPositive(existingReview?.positiveTags || []);
      setSelectedNegative(existingReview?.negativeTags || []);
    }
  }, [isOpen, existingReview]);

  if (!isOpen || !player) return null;

  const isLocked = !!existingReview;
  const totalSelected = selectedPositive.length + selectedNegative.length;
  const reachedMax = totalSelected >= MAX_TAGS_TOTAL;

  const toggle = (tag: string, kind: 'positive' | 'negative') => {
    if (isLocked) return;
    const setter = kind === 'positive' ? setSelectedPositive : setSelectedNegative;
    const list = kind === 'positive' ? selectedPositive : selectedNegative;
    if (list.includes(tag)) {
      setter(list.filter((t) => t !== tag));
      return;
    }
    if (reachedMax) return;
    setter([...list, tag]);
  };

  const handleSave = async () => {
    if (totalSelected === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await onSave({ positive: selectedPositive, negative: selectedNegative });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const renderTag = (tag: string, kind: 'positive' | 'negative') => {
    const selected =
      kind === 'positive'
        ? selectedPositive.includes(tag)
        : selectedNegative.includes(tag);
    const dimmed = !selected && reachedMax;
    const color = kind === 'positive' ? REVIEW_COLORS.positive : REVIEW_COLORS.negative;
    const emoji = TAG_EMOJIS[tag] || '';
    return (
      <button
        type="button"
        key={`${kind}-${tag}`}
        onClick={() => toggle(tag, kind)}
        disabled={isLocked || (dimmed && !selected) || saving}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          borderRadius: '999px',
          border: `2px solid ${selected ? color : theme.colors.border}`,
          background: selected ? color : 'transparent',
          color: selected ? '#fff' : theme.colors.text,
          fontSize: '13px',
          fontWeight: selected ? 600 : 500,
          cursor: isLocked || (dimmed && !selected) || saving ? 'not-allowed' : 'pointer',
          opacity: isLocked && !selected ? 0.3 : dimmed && !selected ? 0.35 : 1,
        }}
      >
        {selected && <span style={{ fontSize: '12px' }}>✓</span>}
        {emoji ? `${emoji} ` : ''}
        {tag}
      </button>
    );
  };

  const initials = player.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? 0 : '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: isMobile ? '20px 20px 0 0' : theme.borderRadius.card,
          padding: theme.spacing.lg,
          maxWidth: isMobile ? '100%' : '520px',
          width: '100%',
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          boxShadow: theme.colors.shadow,
          border: `1px solid ${theme.colors.border}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Handle bar (mobile) */}
        {isMobile && (
          <div
            style={{
              width: '40px',
              height: '4px',
              backgroundColor: theme.colors.border,
              borderRadius: '2px',
              margin: '0 auto 16px',
            }}
          />
        )}

        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            {player.avatar ? (
              <img
                src={player.avatar}
                alt={player.name}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: theme.colors.primary,
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                {initials}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: theme.colors.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {player.name}
              </div>
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: 2 }}>
                {isLocked ? 'Ya lo calificaste' : `${totalSelected}/${MAX_TAGS_TOTAL} tags`}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: theme.colors.textSecondary,
              padding: '4px 8px',
            }}
          >
            ×
          </button>
        </div>

        {/* Positivos */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: REVIEW_COLORS.positive,
              }}
            />
            <h3
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: theme.colors.text,
                margin: 0,
              }}
            >
              Positivos
            </h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {positiveTags.map((t) => renderTag(t, 'positive'))}
          </div>
        </div>

        {/* Negativos */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <span
              style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: REVIEW_COLORS.negative,
              }}
            />
            <h3
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: theme.colors.text,
                margin: 0,
              }}
            >
              Negativos
            </h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {negativeTags.map((t) => renderTag(t, 'negative'))}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.colors.border}`,
            marginTop: 'auto',
          }}
        >
          {!isLocked ? (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '12px 18px',
                  borderRadius: '12px',
                  border: `1px solid ${theme.colors.border}`,
                  background: 'transparent',
                  color: theme.colors.textSecondary,
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                Saltar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || totalSelected === 0}
                style={{
                  flex: 1,
                  padding: '12px 18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: theme.colors.primary,
                  color: '#1a1a1a',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: saving || totalSelected === 0 ? 'not-allowed' : 'pointer',
                  opacity: totalSelected === 0 ? 0.5 : 1,
                }}
              >
                {saving ? 'Guardando...' : `Guardar${totalSelected > 0 ? ` (${totalSelected})` : ''}`}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 18px',
                borderRadius: '12px',
                border: 'none',
                background: theme.colors.primary,
                color: '#1a1a1a',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Listo
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickRateSheet;
